import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Wordmark, CrestMark } from "@/components/Wordmark";
import { Reveal, Eyebrow, SouqMotif } from "@/components/marketing/MarketingPrimitives";
import { SectionHeader } from "@/components/marketing/SectionHeader";
import {
  MockWindow, HeroConsole, OwnershipGraphMini, ComplianceCalendarMini,
  PdfDraftStack, AbacMatrixMini, PerfChartMini,
} from "@/components/marketing/ProductMockup";
import {
  Network, ShieldCheck, Lock, ScrollText, KeyRound, ArrowRight, Check, Globe2,
  FileText, Building2, Landmark, UserCog, TrendingUp, Quote, Menu, Coins,
  Layers, Users, FileCheck2, Scale,
} from "lucide-react";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const NAV_LINKS = [
  { label: "What's New", id: "whats-new" },
  { label: "Capabilities", id: "capabilities" },
  { label: "Security", id: "security" },
  { label: "Pricing", id: "pricing" },
];

/* ============================ Nav ============================ */
function MarketingNav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <header
      data-testid="marketing-nav"
      className={
        "fixed inset-x-0 top-0 z-50 transition-colors duration-300 " +
        (scrolled ? "border-b border-[color:var(--mk-border)] bg-[#070A0F]/80 backdrop-blur-md" : "bg-transparent")
      }
    >
      <div className="mx-auto flex h-[76px] max-w-[var(--container-max)] items-center justify-between px-5 sm:px-8">
        <button onClick={() => scrollTo("top")} className="focus-brass rounded-md" aria-label="Family Souq home">
          <Wordmark size="md" data-testid="marketing-nav-wordmark" />
        </button>
        <nav className="hidden items-center gap-8 md:flex">
          {NAV_LINKS.map((l) => (
            <button
              key={l.id}
              onClick={() => scrollTo(l.id)}
              data-testid={`marketing-nav-${l.id}`}
              className="focus-brass rounded-md px-1 text-sm font-medium text-[color:var(--mk-text-2)] transition-colors hover:text-[color:var(--mk-text)]"
            >
              {l.label}
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <Link to="/login" data-testid="nav-sign-in-button" className="hidden sm:block">
            <Button variant="ghost" className="text-[color:var(--mk-text-2)] hover:text-[color:var(--mk-text)]">Sign in</Button>
          </Link>
          <Button
            onClick={() => scrollTo("request")}
            data-testid="nav-request-access-button"
            className="hidden bg-[color:var(--mk-primary)] text-[color:var(--mk-bg)] hover:bg-[color:var(--mk-primary-2)] sm:inline-flex"
          >
            Request Access
          </Button>
          {/* mobile */}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" size="icon" data-testid="nav-mobile-menu-button" className="border-[color:var(--mk-border)] md:hidden">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="border-[color:var(--mk-border)] bg-[color:var(--mk-bg-2)]">
              <SheetTitle className="sr-only">Family Souq navigation</SheetTitle>
              <div className="mt-2 mb-6"><Wordmark size="md" rule /></div>
              <div className="flex flex-col gap-1">
                {NAV_LINKS.map((l) => (
                  <button key={l.id} onClick={() => { scrollTo(l.id); setOpen(false); }}
                    className="rounded-lg px-3 py-2.5 text-left text-sm text-[color:var(--mk-text-2)] hover:bg-white/[0.04] hover:text-[color:var(--mk-text)]">
                    {l.label}
                  </button>
                ))}
              </div>
              <Separator className="my-4 bg-[color:var(--mk-border)]" />
              <div className="flex flex-col gap-2">
                <Button onClick={() => { scrollTo("request"); setOpen(false); }} className="bg-[color:var(--mk-primary)] text-[color:var(--mk-bg)] hover:bg-[color:var(--mk-primary-2)]">Request Access</Button>
                <Link to="/login"><Button variant="outline" className="w-full border-[color:var(--mk-border)]">Sign in</Button></Link>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}

/* ============================ Hero ============================ */
const HERO_CHIPS = [
  { icon: Landmark, title: "Corporate & MCA", desc: "CIN, directors & cap-table" },
  { icon: ShieldCheck, title: "Statutory Compliance", desc: "Calendar + DRAFT e-forms" },
  { icon: UserCog, title: "Professional Fleet", desc: "ABAC for CA / CS / CMA" },
];

function Hero() {
  const reduce = useReducedMotion();
  return (
    <section id="top" data-testid="marketing-hero" className="relative overflow-hidden tex-grain mk-wash pt-[120px] pb-20 sm:pt-[140px] lg:pb-28">
      <SouqMotif />
      <div className="relative mx-auto grid max-w-[var(--container-max)] grid-cols-1 items-center gap-12 px-5 sm:px-8 lg:grid-cols-2 lg:gap-10">
        <div>
          <Eyebrow>The operating system for Indian family offices</Eyebrow>
          <h1 className="mt-5 font-display text-4xl font-semibold leading-[1.05] tracking-[-0.02em] text-[color:var(--mk-text)] sm:text-5xl lg:text-6xl">
            One private platform for your<span className="text-[color:var(--mk-primary-2)]"> entire wealth empire</span>.
          </h1>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-[color:var(--mk-text-2)]">
            Consolidate multi-entity wealth, run statutory compliance for your companies, and collaborate
            with your chartered accountants and company secretaries — secured by maker-checker governance
            and encrypted key-file access.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Button
              onClick={() => scrollTo("request")}
              data-testid="hero-request-access-button"
              className="group bg-[color:var(--mk-primary)] text-[color:var(--mk-bg)] shadow-[var(--mk-brass-glow)] transition-[background-color,box-shadow] hover:bg-[color:var(--mk-primary-2)]"
            >
              Request a confidential introduction
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Button>
            <Button
              onClick={() => scrollTo("capabilities")}
              variant="outline"
              data-testid="hero-view-capabilities-button"
              className="border-[color:var(--mk-border-strong)] bg-transparent text-[color:var(--mk-text)] hover:bg-white/[0.04]"
            >
              View capabilities
            </Button>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-3 sm:grid-cols-3">
            {HERO_CHIPS.map((c) => (
              <div key={c.title} className="rounded-xl border border-[color:var(--mk-border)] bg-[color:rgba(14,21,34,0.6)] p-3.5 backdrop-blur-sm">
                <c.icon className="h-4 w-4 text-[color:var(--mk-primary-2)]" strokeWidth={2.25} />
                <div className="mt-2 text-sm font-semibold text-[color:var(--mk-text)]">{c.title}</div>
                <div className="text-[11px] text-[color:var(--mk-muted)]">{c.desc}</div>
              </div>
            ))}
          </div>
        </div>

        <motion.div
          initial={reduce ? false : { opacity: 0, y: 24 }}
          animate={reduce ? {} : { opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
          className="relative"
        >
          <div className="pointer-events-none absolute -inset-6 rounded-[28px] bg-[radial-gradient(420px_220px_at_70%_0%,rgba(176,141,87,0.18),transparent_70%)]" />
          <div className="relative">
            <HeroConsole />
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ============================ Trust strip ============================ */
const TRUST = [
  "INR-first consolidation", "Beneficial look-through", "Hash-chained audit trail",
  "Encrypted key-file login", "Maker-checker governance", "Multi-jurisdiction ready",
];
function TrustStrip() {
  return (
    <section data-testid="marketing-trust-strip" className="border-y border-[color:var(--mk-border)] bg-[color:var(--mk-bg-2)] py-5">
      <div className="relative mx-auto max-w-[var(--container-max)] overflow-hidden px-5 sm:px-8">
        <div className="flex w-max items-center gap-10 mk-marquee">
          {[...TRUST, ...TRUST].map((t, i) => (
            <span key={i} className="flex shrink-0 items-center gap-2 text-xs font-medium uppercase tracking-[0.18em] text-[color:var(--mk-muted)]">
              <span className="h-1 w-1 rounded-full bg-[color:var(--mk-primary)]/70" />
              {t}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============================ What's New ============================ */
const NEW_FEATURES = [
  {
    badge: "New",
    icon: Landmark,
    title: "Corporate Entities & MCA",
    desc: "Capture CIN, ROC and capital for Pvt Ltd, HoldCo, OpCo and NBFCs. Tag directors & shareholders to family members — feeding the ownership graph — and keep a per-entity transaction ledger.",
    mockup: (
      <div className="space-y-2">
        <div className="flex items-center justify-between rounded-lg border border-[color:var(--mk-border)] bg-[color:var(--mk-surface-2)] px-3 py-2">
          <span className="text-[11px] text-[color:var(--mk-text-2)]">Mehta Capital Pvt Ltd</span>
          <span className="font-data text-[10px] text-[color:var(--mk-primary-2)]">U67190MH2012PTC123456</span>
        </div>
        <div className="h-[120px] rounded-lg border border-[color:var(--mk-border)] bg-[color:var(--mk-surface-2)] p-2"><OwnershipGraphMini /></div>
      </div>
    ),
  },
  {
    badge: "New",
    icon: ShieldCheck,
    title: "Statutory Compliance, automated",
    desc: "A compliance calendar tuned to each company type, plus auto-generated DRAFT statutory e-forms — AOC-4, MGT-7, DIR-3 KYC, DPT-3, NBS-9/DNBS — rendered as watermarked PDFs ready for your CS to review.",
    mockup: (
      <div className="grid grid-cols-2 gap-2">
        <div className="rounded-lg border border-[color:var(--mk-border)] bg-[color:var(--mk-surface-2)] p-2.5"><ComplianceCalendarMini /></div>
        <div className="rounded-lg border border-[color:var(--mk-border)] bg-[color:var(--mk-surface-2)] p-2.5"><PdfDraftStack /></div>
      </div>
    ),
  },
  {
    badge: "New",
    icon: UserCog,
    title: "Professional fleet with ABAC",
    desc: "Onboard CAs, CMAs, company secretaries, auditors and book-keepers in minutes — with per-entity scope, per-module view/edit rights and time-bound engagements that auto-expire. Each gets an encrypted key-file.",
    mockup: (
      <div className="rounded-lg border border-[color:var(--mk-border)] bg-[color:var(--mk-surface-2)] p-2.5"><AbacMatrixMini /></div>
    ),
  },
];

function WhatsNew() {
  return (
    <section id="whats-new" data-testid="marketing-whats-new" className="mk-surface-wash py-16 sm:py-20 lg:py-24">
      <div className="mx-auto max-w-[var(--container-max)] px-5 sm:px-8">
        <Reveal>
          <SectionHeader
            eyebrow="What's new"
            title="Built for the realities of an Indian family office"
            description="Beyond portfolios — Souq now manages your corporate entities, their statutory filings, and the professionals who serve them."
          />
        </Reveal>
        <div className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-3">
          {NEW_FEATURES.map((f, i) => (
            <Reveal key={f.title} delay={i * 0.08}>
              <div className="group flex h-full flex-col rounded-[18px] border border-[color:var(--mk-border)] bg-[color:var(--mk-surface)] p-5 transition-[transform,border-color,box-shadow] duration-200 hover:-translate-y-1 hover:border-[color:var(--mk-border-strong)] hover:shadow-[var(--mk-shadow-card)]">
                <div className="mb-4 flex items-center justify-between">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[rgba(176,141,87,0.14)]">
                    <f.icon className="h-5 w-5 text-[color:var(--mk-primary-2)]" strokeWidth={2.25} />
                  </span>
                  <Badge className="border-[rgba(176,141,87,0.4)] bg-[rgba(176,141,87,0.16)] text-[10px] uppercase tracking-wider text-[color:var(--mk-primary-2)]">{f.badge}</Badge>
                </div>
                <h3 className="font-display text-lg font-semibold text-[color:var(--mk-text)]">{f.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[color:var(--mk-text-2)]">{f.desc}</p>
                <div className="mt-5">{f.mockup}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============================ Capabilities (splits) ============================ */
function FeatureSplit({ id, testid, eyebrow, title, desc, points, mockup, reverse }) {
  return (
    <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-12">
      <div className={"lg:col-span-5 " + (reverse ? "lg:order-2" : "")}>
        <Reveal>
          <Eyebrow>{eyebrow}</Eyebrow>
          <h3 className="mt-4 font-display text-2xl font-semibold tracking-[-0.01em] text-[color:var(--mk-text)] sm:text-3xl">{title}</h3>
          <p className="mt-4 text-sm leading-relaxed text-[color:var(--mk-text-2)] sm:text-base">{desc}</p>
          <ul className="mt-5 space-y-2.5">
            {points.map((p) => (
              <li key={p} className="flex items-start gap-2.5 text-sm text-[color:var(--mk-text-2)]">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--mk-primary-2)]" />
                {p}
              </li>
            ))}
          </ul>
        </Reveal>
      </div>
      <div className={"lg:col-span-7 " + (reverse ? "lg:order-1" : "")}>
        <Reveal delay={0.1}>
          <MockWindow title={testid.replace("capability-", "souq · ").replace(/-/g, " ")} className="mk-corner-accent" data-testid={testid}>
            {mockup}
          </MockWindow>
        </Reveal>
      </div>
    </div>
  );
}

function Capabilities() {
  return (
    <section id="capabilities" data-testid="marketing-capabilities" className="py-16 sm:py-20 lg:py-24">
      <div className="mx-auto max-w-[var(--container-max)] space-y-20 px-5 sm:px-8 lg:space-y-28">
        <FeatureSplit
          testid="capability-ownership-graph"
          eyebrow="Look-through ownership"
          title="See true exposure, across every entity"
          desc="A beneficial-ownership graph ties each family member to your HoldCo, OpCo and NBFC structures — so you always know who really owns what, even through nested vehicles."
          points={[
            "Family members linked to entities with exact %",
            "Automatic look-through across nested structures",
            "Per-entity ledgers feed consolidated net worth",
          ]}
          mockup={<div className="h-[260px]"><OwnershipGraphMini /></div>}
        />
        <FeatureSplit
          reverse
          testid="capability-performance"
          eyebrow="Private capital + fixed income"
          title="Alternatives and debt, in one view"
          desc="Track PE/VC performance — XIRR, DPI, TVPI — alongside bespoke fixed-income schedules. INR-first, multi-currency ready, with precise day-count math."
          points={[
            "Commitments, calls and distributions lifecycle",
            "XIRR / DPI / TVPI computed automatically",
            "Multi-currency consolidation, INR-first",
          ]}
          mockup={<div className="h-[220px]"><PerfChartMini /></div>}
        />
        <FeatureSplit
          testid="capability-board-packs"
          eyebrow="Reporting"
          title="Board packs, ready to circulate"
          desc="Generate consistently formatted PDF board packs and statutory DRAFT e-forms — auditable, watermarked, and ready for your professionals."
          points={[
            "One-click PDF board packs",
            "Watermarked DRAFT statutory e-forms",
            "Hash-chained audit trail on every change",
          ]}
          mockup={
            <div className="grid grid-cols-2 gap-3">
              <PdfDraftStack />
              <div className="space-y-2 rounded-lg border border-[color:var(--mk-border)] bg-[color:var(--mk-surface-2)] p-3">
                <div className="h-2 w-1/2 rounded bg-[rgba(176,141,87,0.4)]" />
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="flex gap-2">
                    <div className="h-1.5 flex-1 rounded bg-[rgba(231,215,191,0.10)]" />
                    <div className="h-1.5 w-10 rounded bg-[rgba(231,215,191,0.08)]" />
                  </div>
                ))}
              </div>
            </div>
          }
        />
      </div>
    </section>
  );
}

/* ============================ Security ============================ */
const SECURITY = [
  { icon: KeyRound, t: "Encrypted key-file login", d: "A physical 'pendrive' workflow — no passwords to phish." },
  { icon: Scale, t: "Maker-checker approvals", d: "Sensitive changes require a second authorisation." },
  { icon: ScrollText, t: "Hash-chained audit trail", d: "Every action is tamper-evident and verifiable." },
  { icon: UserCog, t: "ABAC for professionals", d: "External advisors see only what they're scoped to." },
  { icon: Lock, t: "Time-bound engagements", d: "Access auto-expires when the engagement ends." },
];
function Security() {
  return (
    <section id="security" data-testid="marketing-security-band" className="relative overflow-hidden border-y border-[color:var(--mk-border)] bg-[color:var(--mk-bg-2)] py-16 sm:py-20 lg:py-24">
      <SouqMotif />
      <div className="relative mx-auto grid max-w-[var(--container-max)] grid-cols-1 gap-12 px-5 sm:px-8 lg:grid-cols-2 lg:items-center">
        <Reveal>
          <Eyebrow>Discretion by design</Eyebrow>
          <h2 className="mt-4 font-display text-2xl font-semibold tracking-[-0.01em] text-[color:var(--mk-text)] sm:text-3xl">
            Security is a feature, not a footnote
          </h2>
          <p className="mt-4 max-w-lg text-sm leading-relaxed text-[color:var(--mk-text-2)] sm:text-base">
            Family wealth demands more than a login. Souq protects sensitive data across generations and
            external advisors with hardware-style access, multi-party authorisation and a verifiable audit trail.
          </p>
        </Reveal>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {SECURITY.map((s, i) => (
            <Reveal key={s.t} delay={i * 0.06}>
              <div className="flex h-full items-start gap-3 rounded-xl border border-[color:var(--mk-border)] bg-[color:var(--mk-surface)] p-4">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[rgba(176,141,87,0.14)]">
                  <s.icon className="h-4 w-4 text-[color:var(--mk-primary-2)]" strokeWidth={2.25} />
                </span>
                <div>
                  <div className="text-sm font-semibold text-[color:var(--mk-text)]">{s.t}</div>
                  <div className="mt-0.5 text-xs leading-relaxed text-[color:var(--mk-muted)]">{s.d}</div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============================ Social proof ============================ */
const QUOTES = [
  { q: "We finally see true look-through exposure across eleven entities in one place — month-end went from a week to an afternoon.", a: "Group CIO", r: "Multi-generational family office, Mumbai" },
  { q: "Onboarding our CA and company secretary with scoped, time-bound access removed our biggest control headache.", a: "CFO", r: "Industrial promoter family, Pune" },
  { q: "Draft AOC-4 and MGT-7 generated straight from the entity data. Our CS just reviews and files.", a: "Family Office Head", r: "NBFC-owning family, Delhi" },
];
function Testimonials() {
  return (
    <section data-testid="marketing-social-proof" className="py-16 sm:py-20 lg:py-24">
      <div className="mx-auto max-w-[var(--container-max)] px-5 sm:px-8">
        <Reveal>
          <SectionHeader align="center" eyebrow="Trusted in confidence" title="Quiet results, spoken plainly" />
        </Reveal>
        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
          {QUOTES.map((t, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <figure className="flex h-full flex-col rounded-[18px] border border-[color:var(--mk-border)] bg-[color:var(--mk-surface)] p-6">
                <Quote className="h-6 w-6 text-[color:var(--mk-primary)]/60" />
                <blockquote className="mt-3 flex-1 font-display text-base leading-relaxed text-[color:var(--mk-text)]">"{t.q}"</blockquote>
                <figcaption className="mt-5 border-t border-[color:var(--mk-border)] pt-4">
                  <div className="text-sm font-semibold text-[color:var(--mk-text)]">{t.a}</div>
                  <div className="text-xs text-[color:var(--mk-muted)]">{t.r}</div>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============================ Pricing ============================ */
const TIERS = [
  {
    name: "Core", tag: "Family Office",
    desc: "Consolidated wealth intelligence for a single family office.",
    features: ["Multi-entity net worth", "Beneficial ownership graph", "Document vault", "Multi-currency, INR-first"],
    featured: false,
  },
  {
    name: "Entities + Compliance", tag: "Most chosen",
    desc: "Everything in Core, plus corporate entities and statutory filings.",
    features: ["Corporate entities & MCA profiles", "Per-entity transaction ledgers", "Statutory compliance calendar", "DRAFT e-forms (AOC-4, MGT-7, NBS-9…)"],
    featured: true,
  },
  {
    name: "Enterprise", tag: "Multi-branch + Fleet",
    desc: "For larger structures and full professional collaboration.",
    features: ["Professional fleet with ABAC", "Maker-checker governance", "Hash-chained audit & board packs", "Priority onboarding & support"],
    featured: false,
  },
];
function Pricing() {
  return (
    <section id="pricing" data-testid="marketing-pricing" className="mk-surface-wash py-16 sm:py-20 lg:py-24">
      <div className="mx-auto max-w-[var(--container-max)] px-5 sm:px-8">
        <Reveal>
          <SectionHeader align="center" eyebrow="By invitation" title="Engagements, not subscriptions"
            description="Souq is offered through a confidential engagement tailored to your structure. Choose a starting scope below." />
        </Reveal>
        <div className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-3">
          {TIERS.map((t, i) => (
            <Reveal key={t.name} delay={i * 0.08}>
              <div className={
                "relative flex h-full flex-col rounded-[18px] border p-6 transition-[transform,box-shadow] duration-200 hover:-translate-y-1 " +
                (t.featured
                  ? "border-[color:var(--mk-primary)]/45 bg-[color:var(--mk-surface)] shadow-[var(--mk-brass-glow)] mk-corner-accent"
                  : "border-[color:var(--mk-border)] bg-[color:var(--mk-surface)]")
              }>
                <div className="flex items-center justify-between">
                  <h3 className="font-display text-xl font-semibold text-[color:var(--mk-text)]">{t.name}</h3>
                  <Badge className={t.featured
                    ? "border-[rgba(176,141,87,0.45)] bg-[rgba(176,141,87,0.18)] text-[10px] uppercase tracking-wider text-[color:var(--mk-primary-2)]"
                    : "border-[color:var(--mk-border-strong)] text-[10px] uppercase tracking-wider text-[color:var(--mk-text-2)]"}>{t.tag}</Badge>
                </div>
                <p className="mt-2 text-sm text-[color:var(--mk-text-2)]">{t.desc}</p>
                <Separator className="my-5 bg-[color:var(--mk-border)]" />
                <ul className="flex-1 space-y-3">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-[color:var(--mk-text-2)]">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--mk-primary-2)]" />{f}
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => scrollTo("request")}
                  data-testid={t.featured ? "pricing-request-access-button" : `pricing-${t.name.toLowerCase().split(" ")[0]}-button`}
                  className={"mt-6 w-full " + (t.featured
                    ? "bg-[color:var(--mk-primary)] text-[color:var(--mk-bg)] hover:bg-[color:var(--mk-primary-2)]"
                    : "border border-[color:var(--mk-border-strong)] bg-transparent text-[color:var(--mk-text)] hover:bg-white/[0.04]")}
                >
                  Request access
                </Button>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============================ Request access form ============================ */
function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", role: "", complexity: "", message: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !/\S+@\S+\.\S+/.test(form.email)) {
      toast.error("Please share your name and a valid email.");
      return;
    }
    toast.success("Request received — our team will reach out confidentially.");
    setForm({ name: "", email: "", phone: "", role: "", complexity: "", message: "" });
  };
  return (
    <section id="request" className="py-16 sm:py-20 lg:py-24">
      <div className="mx-auto grid max-w-[var(--container-max)] grid-cols-1 gap-12 px-5 sm:px-8 lg:grid-cols-2 lg:items-center">
        <Reveal>
          <Eyebrow>Request access</Eyebrow>
          <h2 className="mt-4 font-display text-2xl font-semibold tracking-[-0.01em] text-[color:var(--mk-text)] sm:text-3xl">
            A confidential introduction
          </h2>
          <p className="mt-4 max-w-md text-sm leading-relaxed text-[color:var(--mk-text-2)] sm:text-base">
            Tell us a little about your family office. We onboard a limited number of families each quarter,
            with white-glove setup of your entities, holdings and professional fleet.
          </p>
          <div className="mt-6 flex items-center gap-2 text-xs text-[color:var(--mk-muted)]">
            <Lock className="h-3.5 w-3.5" /> Your details are kept strictly confidential.
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <form onSubmit={onSubmit} data-testid="request-access-form" className="mk-card p-6 sm:p-8">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-xs uppercase tracking-[0.12em] text-[color:var(--mk-muted)]">Name</Label>
                <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="request-access-name" placeholder="Your full name" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.12em] text-[color:var(--mk-muted)]">Role</Label>
                <Select value={form.role} onValueChange={(v) => set("role", v)}>
                  <SelectTrigger data-testid="request-access-role-select"><SelectValue placeholder="Select role" /></SelectTrigger>
                  <SelectContent>
                    {["Principal", "CIO", "CFO", "Family Office Head", "Chartered Accountant", "Company Secretary", "Cost Accountant", "Other"].map((r) => (
                      <SelectItem key={r} value={r}>{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.12em] text-[color:var(--mk-muted)]">Entity complexity</Label>
                <Select value={form.complexity} onValueChange={(v) => set("complexity", v)}>
                  <SelectTrigger data-testid="request-access-entity-complexity-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {["1–3 entities", "4–10 entities", "11–25 entities", "25+ entities / multi-branch"].map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.12em] text-[color:var(--mk-muted)]">Email</Label>
                <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="request-access-email" placeholder="name@familyoffice.in" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.12em] text-[color:var(--mk-muted)]">Phone (optional)</Label>
                <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="request-access-phone" placeholder="+91" />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-xs uppercase tracking-[0.12em] text-[color:var(--mk-muted)]">Message</Label>
                <Textarea rows={3} value={form.message} onChange={(e) => set("message", e.target.value)} data-testid="request-access-message" placeholder="A little about your structure and goals…" />
              </div>
            </div>
            <Button type="submit" data-testid="request-access-submit-button"
              className="mt-6 w-full bg-[color:var(--mk-primary)] text-[color:var(--mk-bg)] hover:bg-[color:var(--mk-primary-2)]">
              Request a confidential introduction
            </Button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}

/* ============================ Footer ============================ */
function Footer() {
  return (
    <footer data-testid="marketing-footer" className="relative overflow-hidden border-t border-[color:var(--mk-border)] bg-[color:var(--mk-bg-2)]">
      <SouqMotif />
      <div className="relative mx-auto max-w-[var(--container-max)] px-5 py-14 sm:px-8">
        <div className="flex flex-col items-start justify-between gap-8 lg:flex-row">
          <div className="max-w-sm">
            <Wordmark size="lg" rule />
            <p className="mt-4 text-sm leading-relaxed text-[color:var(--mk-text-2)]">
              The private operating system for Indian family offices — wealth, entities, compliance and people, in one discreet place.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-x-12 gap-y-3 sm:grid-cols-3">
            {[
              ["Platform", [["What's New", "whats-new"], ["Capabilities", "capabilities"], ["Security", "security"], ["Pricing", "pricing"]]],
              ["Company", [["Request access", "request"]]],
            ].map(([head, items]) => (
              <div key={head}>
                <div className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-[color:var(--mk-muted)]">{head}</div>
                <ul className="space-y-2">
                  {items.map(([l, id]) => (
                    <li key={l}>
                      <button onClick={() => scrollTo(id)} className="text-sm text-[color:var(--mk-text-2)] transition-colors hover:text-[color:var(--mk-text)]">{l}</button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
            <div>
              <div className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-[color:var(--mk-muted)]">Access</div>
              <Link to="/login" className="text-sm text-[color:var(--mk-text-2)] transition-colors hover:text-[color:var(--mk-text)]">Sign in</Link>
            </div>
          </div>
        </div>
        <Separator className="my-8 bg-[color:var(--mk-border)]" />
        <div className="flex flex-col items-center justify-between gap-3 text-xs text-[color:var(--mk-muted)] sm:flex-row">
          <span>© {new Date().getFullYear()} Family Souq. Private & confidential.</span>
          <span className="flex items-center gap-2"><Globe2 className="h-3.5 w-3.5" /> INR-first · Multi-jurisdiction ready</span>
        </div>
      </div>
    </footer>
  );
}

export default function Landing() {
  return (
    <div className="theme-marketing-dark min-h-screen font-sans antialiased">
      <MarketingNav />
      <main>
        <Hero />
        <TrustStrip />
        <WhatsNew />
        <Capabilities />
        <Security />
        <Testimonials />
        <Pricing />
        <Contact />
      </main>
      <Footer />
    </div>
  );
}
